import {
  giftablePlans,
  PLAN_GRANT_MAX_MONTHS,
  type GiftablePlan,
  type SalonPlanGrantResult,
  type SalonRow,
  type SalonsResponse,
} from '@manaro/contracts'
import { formatDate } from '@/shared/lib/format.ts'
import { planLabel } from './labels.ts'

type Salon = NonNullable<SalonRow['salon']>

/** Quick picks in the dialog: the usual sales deals. */
export const PLAN_GRANT_PRESETS = [1, 3, 6] as const

const pad = (value: number): string => String(value).padStart(2, '0')

/** YYYY-MM-DD in local time, the format of <input type="date">. */
export const toDateInput = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

/** Months ahead without spilling over: 31 January + 1 month is 28 (29) February, as on the backend. */
export function addMonths(date: Date, months: number): Date {
  const first = new Date(date.getFullYear(), date.getMonth() + months, 1)
  const lastDay = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()
  return new Date(first.getFullYear(), first.getMonth(), Math.min(date.getDate(), lastDay))
}

/** The dates the backend accepts: from tomorrow up to a year ahead. */
export function grantDateBounds(now: Date): { min: string; max: string } {
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
  return { min: toDateInput(tomorrow), max: toDateInput(addMonths(now, PLAN_GRANT_MAX_MONTHS)) }
}

const isGiftable = (plan: string | null): plan is GiftablePlan =>
  plan !== null && (giftablePlans as readonly string[]).includes(plan)

/** Extending a gift keeps its plan; anyone else starts from Solo. */
export const defaultGiftPlan = (salon: Salon): GiftablePlan => (isGiftable(salon.plan) ? salon.plan : 'solo')

/** A paid period is the salon's money: the backend refuses to replace it with a gift. */
export function hasPaidPeriod(salon: Salon, now: Date): boolean {
  if (salon.planGift || !isGiftable(salon.plan) || !salon.planRenewsAt) return false
  return new Date(salon.planRenewsAt).getTime() > now.getTime()
}

/** Patches the one salon in the cached list instead of recounting everything. */
export function applyPlanGrant(data: SalonsResponse, result: SalonPlanGrantResult): SalonsResponse {
  return {
    ...data,
    rows: data.rows.map((row) =>
      row.salon?.id === result.salonId
        ? {
            ...row,
            salon: { ...row.salon, plan: result.plan, planRenewsAt: result.planRenewsAt, planGift: true, pendingPlan: null },
          }
        : row,
    ),
  }
}

export function planGrantNotice(title: string, result: SalonPlanGrantResult): string {
  const until = result.planRenewsAt ? ` до ${formatDate(result.planRenewsAt)}` : ''
  return `«${title}»: ${planLabel(result.plan)} бесплатно${until} — потом салон перейдёт на бесплатный тариф, если не оплатит.`
}
